import { useCallback } from 'react';
import { useDockviewApi } from './dockview-context.js';
import { useAppStore, usePanel, type PanelId, type TabId } from './store.js';
import type { PanelComponentKey, PanelParams } from './components.js';

// Opens a panel on the SAME link CHANNEL as `(tabId, panelId)`, so channel-scoped
// broadcasts (catchment-link, rollup-link, entity selection) flow between them.
// Powers the "open the downstream view" buttons: Roll-Up → Catchment, Catchment
// → Census. Dedup is DOCKVIEW-authoritative — if a live panel of `component` is
// already on this channel we focus it instead of duplicating (the store can hold
// orphan records that aren't rendered, so a store-only check would lie).
// Otherwise we spawn one floating (the user can dock it). Matches the ask: open
// one only if there isn't a linked one in the tab already.
export function useOpenChannelPanel(
  tabId: TabId,
  panelId: PanelId,
  component: PanelComponentKey,
  title: string,
  size: { width: number; height: number } = { width: 760, height: 520 },
  /** Seed params for a NEWLY-opened panel (ignored when focusing an existing
   *  one). Pass a STABLE reference (module const / useMemo) — it's a hook dep. */
  initialState?: Record<string, unknown>,
): () => void {
  const apiRef = useDockviewApi();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const panel = usePanel(tabId, panelId);
  const groupId = panel
    ? panel.link.kind === 'linked'
      ? panel.link.groupId
      : panel.link.lastGroupId
    : 'default';
  const { width, height } = size;

  return useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const tab = useAppStore.getState().tabs[tabId];
    const existing = api.panels.find((p) => {
      if (p.api.component !== component) return false;
      const sp = tab?.panels[p.id];
      const gid = sp ? (sp.link.kind === 'linked' ? sp.link.groupId : sp.link.lastGroupId) : null;
      return gid === groupId;
    });
    if (existing) {
      existing.api.setActive();
      return;
    }
    const newId = addPanelToStore(tabId, { component, title, groupId });
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel<PanelParams>({
      id: newId,
      component,
      title,
      params: { tabId, panelId: newId, ...(initialState ? { initialState } : {}) },
      floating: { position: { left: 120 + offset, top: 80 + offset }, width, height },
    });
  }, [apiRef, addPanelToStore, tabId, groupId, component, title, width, height, initialState]);
}
